import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import CustomerDataModal from '../CustomerDataModal/CustomerDataModal.component';
import { SmallProjectModal } from '../SmallProjectModal/SmallProjectModal.component';
import * as St from './SaveEstimationButtons.styles';

import {
  DEFAULT_SAVE_ACTION,
  EE_STATUS,
  LOCAL_STORAGE_KEY,
  STATUS,
  SaveActionType,
} from '~/core/constants';
import {
  isReadyForPayment,
  updateCurrentUrlSearchParams,
} from '~/core/helpers';
import useEstimateSaver from '~/core/hooks/useEstimateSaver';
import useEstimateStatus from '~/core/hooks/useEstimateStatus';
import useResetableState from '~/core/hooks/useResetableState';
import { useRetailerSettings } from '~/core/hooks/useRetailerSettings';
import { useSmallJobs } from '~/core/hooks/useSmallJobs';
import { useEstimateContext } from '~/legacy/easy-estimator-page/context/estimateContext';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { updateSaveAction } from '~/redux/slices/customerData';
import {
  resetSmallProjectTicketNumber,
  selectEeStatusList,
  selectIsCompletedEstimate,
  updateValidSmallTicketNumber,
} from '~/redux/slices/estimate';
import CommonButton from '~atoms/CommonButton';
import CustomerExpectationsCheck from '~molecules/CustomerExpectationsCheck';
import CustomerExpectationModal from '~organisms/CustomerExpectationModal';

enum SaveEstimateModalType {
  /**
   * Modal to allow the user to input a ticket number to get a better price for small projects
   */
  SMALL_PROJECT = 'SMALL_PROJECT',

  /**
   * Modal to collect customer data
   */
  CUSTOMER_DATA = 'CUSTOMER_DATA',
}

const SaveEstimationButtons = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const { estimateStatus } = useEstimateStatus();
  const { fetchedEstimate } = useEstimateContext();
  const { canOverrideSmallJob } = useSmallJobs();
  const isCompletedEstimate = useAppSelector(selectIsCompletedEstimate);
  const eeStatusList = useAppSelector(selectEeStatusList);
  const isSaveBlocked = eeStatusList.includes(EE_STATUS.NO_SAVE);
  const { isEstimateDirty } = useEstimateSaver();
  const { retailerSettings } = useRetailerSettings();

  const [activeModal, setActiveModal, resetActiveModal] = useResetableState<
    SaveEstimateModalType | undefined
  >(undefined);

  const [agreementSelected, setAgreementSelected] = useState(false);
  const [isCustomerExpectationModalOpen, setIsCustomerExpectationModalOpen] =
    useState(false);

  const showPaymentBtn =
    !!fetchedEstimate?.paymentUrl &&
    isReadyForPayment(estimateStatus, fetchedEstimate);

  const isExpired = fetchedEstimate?.status === STATUS.EXPIRED;
  const showSaveButtons = !isCompletedEstimate && isEstimateDirty && !isExpired;

  const handleSaveButtonClick = (saveAction: SaveActionType) => {
    dispatch(updateSaveAction(saveAction));
    openModalForSmallProjectOrCustomerData();
  };

  const openModalForSmallProjectOrCustomerData = () => {
    if (canOverrideSmallJob) {
      setActiveModal(SaveEstimateModalType.SMALL_PROJECT);
    } else {
      setActiveModal(SaveEstimateModalType.CUSTOMER_DATA);
    }
  };

  const cancelSmallProject = () => {
    resetActiveModal();
  };

  const saveSmallProject = () => {
    dispatch(resetSmallProjectTicketNumber()); // make certain there's no ticket number
    setActiveModal(SaveEstimateModalType.CUSTOMER_DATA);
  };

  const validateSmallProject = (ticketNumber: string) => {
    dispatch(updateValidSmallTicketNumber(ticketNumber));
    setActiveModal(SaveEstimateModalType.CUSTOMER_DATA);
  };

  const handleCreateNewEstimative = () =>
    (window.location.href = updateCurrentUrlSearchParams({
      [LOCAL_STORAGE_KEY.LOAD_ESTIMATE]: undefined,
    }).toString());

  return (
    <St.Container>
      {showPaymentBtn && !isEstimateDirty && (
        <St.ButtonContainer>
          <CommonButton
            onClick={() => {
              window.open(fetchedEstimate.paymentUrl);
            }}
          >
            {t('ORDER_SUMMARY.ESTIMATE_SECTION.PAYMENT_BUTTON')}
          </CommonButton>
        </St.ButtonContainer>
      )}

      {isCompletedEstimate && (
        <St.ButtonContainer>
          <CommonButton onClick={handleCreateNewEstimative}>
            {t('ESTIMATE_LIST.CREATE')}
          </CommonButton>
        </St.ButtonContainer>
      )}

      {showSaveButtons && (
        <>
          <CustomerExpectationsCheck
            isChecked={agreementSelected}
            setIsChecked={setAgreementSelected}
            setIsCustomerExpectationModalOpen={
              setIsCustomerExpectationModalOpen
            }
          />

          <St.ButtonContainer>
            <CommonButton
              disabled={!agreementSelected || isSaveBlocked}
              onClick={() => handleSaveButtonClick(DEFAULT_SAVE_ACTION)}
            >
              {t('BUTTON.SAVE')}
            </CommonButton>

            {retailerSettings?.saveActions?.map((action) => (
              <CommonButton
                key={action.saveAction}
                disabled={!agreementSelected || isSaveBlocked}
                onClick={() => handleSaveButtonClick(action.saveAction)}
              >
                {action.description}
              </CommonButton>
            ))}
          </St.ButtonContainer>
        </>
      )}

      {activeModal === SaveEstimateModalType.SMALL_PROJECT && (
        <SmallProjectModal
          isVisible={activeModal === SaveEstimateModalType.SMALL_PROJECT}
          onContinue={saveSmallProject}
          onSuccess={validateSmallProject}
          onCancel={cancelSmallProject}
        />
      )}

      <CustomerDataModal
        isOpen={activeModal === SaveEstimateModalType.CUSTOMER_DATA}
        handleClose={resetActiveModal}
      />
      <CustomerExpectationModal
        isVisible={isCustomerExpectationModalOpen}
        setIsVisible={setIsCustomerExpectationModalOpen}
        setCustomerExpectationModelSelected={setIsCustomerExpectationModalOpen}
        setAgreementSelected={setAgreementSelected}
      />
    </St.Container>
  );
};

export default SaveEstimationButtons;
